import type { EmojiEntry, EmojiGroup } from "./emoji-data.js";

/** Strip diacritics + lowercase so a search in one language ("froehlich" /
 *  "fröhlich") and another hit the same normalized keyword bag. */
export const normalizeQuery = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export interface EmojiIndex {
  groups: EmojiGroup[];
  /** Renderable entries only (tofu/unsupported glyphs removed). */
  data: EmojiEntry[];
  /** `data` bucketed by group index. */
  byGroup: EmojiEntry[][];
  /** Curated quick-pick, filtered to renderable glyphs. */
  frequent: string[];
  /** Normalized keyword bag per `data` entry — built lazily on first search so
   *  it stays off the initial open path. */
  keywords(): string[];
}

let cache: Promise<EmojiIndex> | null = null;

/** Lazily load the (large, all-language) emoji dataset as its own chunk, then
 *  prepare the browse index and drop glyphs the current platform can't render.
 *  Cached — the heavy work happens once per session. */
export function loadEmojiIndex(): Promise<EmojiIndex> {
  return (cache ??= build());
}

async function build(): Promise<EmojiIndex> {
  const { EMOJI_DATA, EMOJI_GROUPS, FREQUENT_EMOJI } = await import("./emoji-data.js");
  const supported = await detectSupported([...FREQUENT_EMOJI, ...EMOJI_DATA.map((e) => e[0])]);
  const data = EMOJI_DATA.filter((e) => supported.has(e[0]));
  const byGroup: EmojiEntry[][] = EMOJI_GROUPS.map(() => []);
  for (const entry of data) byGroup[entry[1]]?.push(entry);
  const frequent = FREQUENT_EMOJI.filter((e) => supported.has(e));

  let kw: string[] | null = null;
  return {
    groups: EMOJI_GROUPS,
    data,
    byGroup,
    frequent,
    keywords: () => (kw ??= data.map((e) => normalizeQuery(e[2]))),
  };
}

// ── Platform glyph support ──────────────────────────────────────────────────
// Bump when the detection algorithm changes so stale caches are ignored.
const ALGO = 3;
const CACHE_KEY = "yc:emoji-unsupported";

/** Keep only glyphs that draw as a real emoji — dropping the `.notdef` "tofu"
 *  box (unsupported codepoints) and broken ZWJ sequences that fall back to
 *  several side-by-side glyphs. Platform-accurate (it measures what THIS
 *  device's font paints) and cached in localStorage so it runs at most once. */
async function detectSupported(chars: string[]): Promise<Set<string>> {
  const supported = new Set(chars);
  if (typeof document === "undefined") return supported; // SSR: keep everything

  const cached = readCache(chars.length);
  if (cached) {
    for (const c of cached) supported.delete(c);
    return supported;
  }

  const em = 20;
  const cols = 16; // glyphs per row on the work canvas
  const cellW = em * 2; // room for a too-wide fallback to reach the cell edge
  const cellH = em;
  const batch = cols * cols; // 256 glyphs per getImageData — far fewer readbacks
  const maxContentWidth = em * 1.5; // wider than one glyph ⇒ fallback sequence

  const canvas = document.createElement("canvas");
  canvas.width = cols * cellW;
  canvas.height = cols * cellH;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return supported;
  ctx.textBaseline = "top";
  ctx.font = `${em}px sans-serif`;

  const drawClipped = (s: string, x: number, y: number) => {
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, cellW, cellH);
    ctx.clip();
    ctx.fillText(s, x, y);
    ctx.restore();
  };

  // Reference "tofu" box (a noncharacter → guaranteed .notdef) as a cell of alpha.
  ctx.clearRect(0, 0, cellW, cellH);
  drawClipped("￿", 0, 0);
  const tofuImg = ctx.getImageData(0, 0, cellW, cellH).data;
  const tofuA = new Uint8ClampedArray(cellW * cellH);
  for (let i = 0; i < tofuA.length; i++) tofuA[i] = tofuImg[i * 4 + 3]!;

  const unsupported: string[] = [];
  for (let start = 0; start < chars.length; start += batch) {
    const slice = chars.slice(start, start + batch);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (let i = 0; i < slice.length; i++) {
      drawClipped(slice[i]!, (i % cols) * cellW, ((i / cols) | 0) * cellH);
    }
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    const W = canvas.width;
    for (let i = 0; i < slice.length; i++) {
      const x0 = (i % cols) * cellW;
      const y0 = ((i / cols) | 0) * cellH;
      let blank = true;
      let sameAsTofu = true;
      let rightMost = 0;
      for (let y = 0; y < cellH; y++) {
        for (let x = 0; x < cellW; x++) {
          const a = img[((y0 + y) * W + (x0 + x)) * 4 + 3]!;
          if (a !== 0) {
            blank = false;
            if (x > rightMost) rightMost = x;
          }
          if (a !== tofuA[y * cellW + x]) sameAsTofu = false;
        }
      }
      if (blank || sameAsTofu || rightMost > maxContentWidth) unsupported.push(slice[i]!);
    }
    // Yield between batches (~8 total) so the sweep can't freeze the frame.
    await new Promise((r) => setTimeout(r));
  }

  for (const c of unsupported) supported.delete(c);
  writeCache(chars.length, unsupported);
  return supported;
}

function readCache(n: number): string[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { ua: string; n: number; algo: number; list: string[] };
    if (v.algo === ALGO && v.n === n && v.ua === navigator.userAgent) return v.list;
  } catch {
    /* private mode / quota / parse — just re-detect */
  }
  return null;
}

function writeCache(n: number, list: string[]): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ua: navigator.userAgent, n, algo: ALGO, list }));
  } catch {
    /* ignore */
  }
}
